/**
 * Next.js Library Processor
 *
 * Handles Next.js navigation hooks:
 * - useRouter: Navigation and routing control (output)
 * - usePathname: Current pathname access (input)
 * - useSearchParams: URL search parameters access (input)
 * - useParams: Dynamic route parameters access (input)
 *
 * This processor implements URL node sharing logic where:
 * - Input hooks (usePathname, useSearchParams, useParams) share a single "URL: Input" node
 * - Output hooks (useRouter) share a single "URL: Output" node
 * - URL nodes are reset when processing a new component
 */
/**
 * Next.js library processor
 * Handles useRouter, usePathname, useSearchParams, and useParams hooks
 */
export class NextJSLibraryProcessor {
    metadata = {
        id: 'next',
        libraryName: 'next/navigation',
        packagePatterns: ['next/navigation'],
        hookNames: ['useRouter', 'usePathname', 'useSearchParams', 'useParams'],
        priority: 50, // Medium priority for third-party libraries
        description: 'Next.js navigation hooks processor'
    };
    /**
     * Shared URL node IDs for singleton pattern
     * These are reset when processing a new component
     */
    urlInputNodeId = null;
    urlOutputNodeId = null;
    /**
     * Check if this processor should handle the given hook
     */
    shouldHandle(hook, context) {
        // Check if hook is from Next.js navigation library
        const enrichedHook = hook;
        return (enrichedHook.libraryName === 'next/navigation' &&
            this.metadata.hookNames.includes(hook.hookName));
    }
    /**
     * Process the hook and return DFD elements
     */
    process(hook, context) {
        const { logger, generateNodeId } = context;
        logger.start(hook.hookName, hook);
        const nodes = [];
        const edges = [];
        // Determine if this is an input or output hook
        const isOutputHook = hook.hookName === 'useRouter';
        const isInputHook = hook.hookName === 'usePathname' ||
            hook.hookName === 'useSearchParams' ||
            hook.hookName === 'useParams';
        // Create the hook node itself (as a data-store for library hooks)
        const nodeId = generateNodeId('library_hook');
        const label = hook.hookName;
        // For Next.js hooks, we need to track which variables are data vs process properties
        // useRouter returns an object with methods (process properties)
        // usePathname returns a string (data)
        // useSearchParams returns a SearchParams object (data - can be used directly in JSX)
        // useParams returns a params object (data - can be used directly in JSX)
        const dataProperties = [];
        const processProperties = [];
        if (isOutputHook) {
            // useRouter returns an object with methods like push(), back(), etc.
            // The variable itself (e.g., 'router') is what processes will reference
            processProperties.push(...hook.variables);
        }
        else if (isInputHook) {
            // All input hooks return data that can be used directly in JSX
            // usePathname: returns string
            // useSearchParams: returns SearchParams object (can be used in JSX)
            // useParams: returns params object (can be used in JSX)
            dataProperties.push(...hook.variables);
        }
        const node = {
            id: nodeId,
            label,
            type: 'data-store',
            line: hook.line,
            column: hook.column,
            metadata: {
                category: 'library-hook',
                hookName: hook.hookName,
                libraryName: 'next/navigation',
                isLibraryHook: true,
                isNextJSHook: true,
                // Store variable names for process reference tracking
                variables: hook.variables,
                dataProperties,
                processProperties,
                line: hook.line,
                column: hook.column
            }
        };
        nodes.push(node);
        logger.node('created', node);
        // Create or reuse URL: Input node for input hooks
        if (isInputHook) {
            if (!this.urlInputNodeId) {
                this.urlInputNodeId = generateNodeId('url_input');
                const urlInputNode = {
                    id: this.urlInputNodeId,
                    label: 'URL: Input',
                    type: 'external-entity-input',
                    metadata: {
                        category: 'external-entity',
                        isURLInput: true
                    }
                };
                nodes.push(urlInputNode);
                logger.node('created', urlInputNode);
            }
            else {
                logger.debug(`Reusing existing URL: Input node: ${this.urlInputNodeId}`);
            }
            // Create edge from URL: Input to hook
            const edge = {
                from: this.urlInputNodeId,
                to: nodeId,
                label: 'provides'
            };
            edges.push(edge);
            logger.edge('created', edge);
        }
        // Create or reuse URL: Output node for output hooks
        if (isOutputHook) {
            if (!this.urlOutputNodeId) {
                this.urlOutputNodeId = generateNodeId('url_output');
                const urlOutputNode = {
                    id: this.urlOutputNodeId,
                    label: 'URL: Output',
                    type: 'external-entity-output',
                    metadata: {
                        category: 'external-entity',
                        isURLOutput: true
                    }
                };
                nodes.push(urlOutputNode);
                logger.node('created', urlOutputNode);
            }
            else {
                logger.debug(`Reusing existing URL: Output node: ${this.urlOutputNodeId}`);
            }
            // Create edge from hook to URL: Output
            const edge = {
                from: nodeId,
                to: this.urlOutputNodeId,
                label: 'navigates'
            };
            edges.push(edge);
            logger.edge('created', edge);
        }
        logger.complete({ nodes, edges, handled: true });
        return { nodes, edges, handled: true };
    }
    /**
     * Reset the shared URL node IDs
     * Call this when starting a new component analysis
     */
    reset() {
        this.urlInputNodeId = null;
        this.urlOutputNodeId = null;
    }
}
//# sourceMappingURL=next.js.map